import { AjaxClient } from "app/common/ajax";
import * as Bowser from "bowser";
import * as $ from "jquery";

const buttonId = "enable-push-notifications";
const tableId = "push-subscriptions-table";
const countId = "push-subscriptions-count";
const emptyRowId = "push-subscriptions-empty-row";
const serverWorkerScriptUrl = "/assets/js/service-worker.js";

export function initializePushSubscriptionList(
    ajaxClient: AjaxClient,
    applicationServerPublicKey: string,
): void {
    const button = $(`#${buttonId}`);

    button.on("click", () => {
        button.attr("disabled", "true");
        // Note that we only catch here, to make sure the user can retry if adding a push subscription fails.
        // Users should only subscribe once per device, so keeping the button disabled on success is intended behaviour.
        createPushSubscription(ajaxClient, applicationServerPublicKey)
            .catch(() => button.removeAttr("disabled"));
    });
}

async function createPushSubscription(ajaxClient: AjaxClient, applicationServerPublicKey: string): Promise<void> {
    await ensureBrowserSupport();
    await ensureNotificationPermissionGranted();

    const registration = await registerServiceWorker();
    const subscription = await subscribeToPushNotifications(applicationServerPublicKey, registration.pushManager);
    const name = getReminderName();
    await saveSubscription(ajaxClient, subscription, name);
    addSubscriptionToUi(name);
}

async function ensureBrowserSupport(): Promise<void> {
    // TODO(#773): implement
    return Promise.resolve();
}

async function ensureNotificationPermissionGranted(): Promise<void> {
    const permissionResult = await requestNotificationPermission();
    switch (permissionResult) {
        case "granted":
            return;
        case "default":
        case "denied":
            // TODO(#774): show some nicer messaging
            throw new Error("No notification permission granted.");
        default:
            throw new Error("Unexpected result from notification permission request.");
    }
}

function requestNotificationPermission(): Promise<NotificationPermission> {
    /* eslint-disable @typescript-eslint/no-misused-promises -- See comment below */
    // The official API says that Notification.requestPermission should return a Promise. However, older browsers (most
    // notably Safari) still implement an old version of this API, which takes a callback function instead.
    // This method makes sure that the rest of the code can use this code as a Promise.
    return new Promise((resolve, reject) => {
        const permissionResult = Notification.requestPermission((result) => {
            resolve(result);
        });

        if (permissionResult) {
            return permissionResult.then(resolve, reject);
        }
    });
    /* eslint-enable @typescript-eslint/no-misused-promises */
}

async function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
    return await navigator.serviceWorker.register(serverWorkerScriptUrl);
}

async function subscribeToPushNotifications(
    applicationServerPublicKey: string, pushManager: PushManager,
): Promise<PushSubscription> {
    const subscribeOptions = {
        applicationServerKey: urlBase64ToUint8Array(applicationServerPublicKey),
        userVisibleOnly: true,
    };
    return await pushManager.subscribe(subscribeOptions);
}

function urlBase64ToUint8Array(base64String: string) {
    const padding = "=".repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
        .replace(/-/g, "+")
        .replace(/_/g, "/");
    const rawData = window.atob(base64);
    return Uint8Array.from(Array.from(rawData).map((char) => char.charCodeAt(0)));
}

function getReminderName(): string {
    const browser = Bowser.getParser(window.navigator.userAgent);
    return `${browser.getBrowserName()} on ${browser.getOSName()}`;
}

async function saveSubscription(ajaxClient: AjaxClient, subscription: PushSubscription, name: string): Promise<void> {
    await ajaxClient.call("/account/push-subscriptions/add", {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        auth_key: arrayBufferToBase64(subscription.getKey("auth")),
        endpoint: subscription.endpoint,
        name,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        p256dh_key: arrayBufferToBase64(subscription.getKey("p256dh")),
    });
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
    return btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(buffer))));
}

function addSubscriptionToUi(name: string): void {
    const tableBody = $(`#${tableId} tbody`);
    const newRow = $(`
        <tr>
            <td>${name}</td>
            <td class="text-small">Now</td>
            <td></td>
        </tr>`);
    tableBody.append(newRow);
    $(`#${emptyRowId}`).remove();
    $(`#${countId}`).text(`${tableBody.find("tr").length}`);
}
